import React, { useEffect, useState } from "react";
import { Table, Tag, Button, Modal, Tooltip, message } from "antd";
import { LockOutlined } from "@ant-design/icons";
import {
  getOversight, getOversightLog, createOversight, updateOversight, deleteOversight,
} from "api/oversight/oversightApi";
import OversightEditor from "./OversightEditor";

// THE OVERSIGHT ENTRY ON THE ROSTER, READ-ONLY (owner brief 2026-10-11 T40:
// the project owner and the owning company's admins see it, labelled
// "Oversight - <Company>", and cannot change it - only a super admin can).
// The activity log (T42) opens from here for whoever the server lets read it;
// for anyone else the button is simply not there.
//
// A SUPER ADMIN (the server answers canEdit) also gets Add / Edit / Remove
// here, through OversightEditor. Nobody else sees those controls, and the
// server refuses the writes for them regardless.
export default function OversightPanel({ projectId }) {
  const [entries, setEntries] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [editing, setEditing] = useState(null); // null closed, "new", or an entry
  const [reload, setReload] = useState(0);
  const [logOpen, setLogOpen] = useState(false);
  const [log, setLog] = useState(null);
  const [logAllowed, setLogAllowed] = useState(false);
  const [logLoading, setLogLoading] = useState(false);

  useEffect(() => {
    let dead = false;
    if (!projectId) return undefined;
    getOversight(projectId)
      .then((d) => {
        if (dead) return;
        setEntries(Array.isArray(d && d.entries) ? d.entries : []);
        setCanEdit(!!(d && d.canEdit));
        setCatalog(Array.isArray(d && d.permissionCatalog) ? d.permissionCatalog : []);
      })
      .catch(() => { if (!dead) { setEntries([]); setCanEdit(false); } });
    // Asked once up front so the button only shows for readers of the log.
    getOversightLog(projectId, { limit: 1 })
      .then(() => { if (!dead) setLogAllowed(true); })
      .catch(() => { if (!dead) setLogAllowed(false); });
    return () => { dead = true; };
  }, [projectId, reload]);

  const saveEntry = async (body) => {
    if (editing === "new") await createOversight(projectId, body);
    else await updateOversight(projectId, editing._id, body);
    setEditing(null);
    setReload((n) => n + 1);
    message.success("Oversight entry saved");
  };

  const removeEntry = (e) => {
    Modal.confirm({
      title: `Remove ${e.label}?`,
      content: "Its members lose the access the entry gave them on this project. The removal is written to the activity log.",
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: () => deleteOversight(projectId, e._id)
        .then(() => { setReload((n) => n + 1); message.success("Oversight entry removed"); })
        .catch((err) => message.error((err && err.message) || "The entry could not be removed")),
    });
  };

  const openLog = () => {
    setLogOpen(true);
    setLogLoading(true);
    getOversightLog(projectId, { limit: 500 })
      .then((d) => setLog(Array.isArray(d && d.rows) ? d.rows : []))
      .catch(() => setLog([]))
      .finally(() => setLogLoading(false));
  };

  if (!entries.length && !canEdit) return null;

  return (
    <div style={{ marginTop: 28 }}>
      {canEdit ? (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
          <Button size="small" onClick={() => setEditing("new")}>Add oversight entry</Button>
        </div>
      ) : null}
      {entries.map((e) => (
        <div key={e._id} style={{ border: "1px solid #E4E7EC", borderRadius: 8, padding: "12px 14px", marginBottom: 12, background: "#FAFAFB" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <h3 style={{ margin: 0 }}>{e.label}</h3>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Tooltip title="Only a platform super admin can add, change or remove this entry, its members or their permissions.">
                <Tag icon={<LockOutlined />}>Locked</Tag>
              </Tooltip>
              {e.dormant ? (
                <Tooltip title="This company owns the project now. The entry takes effect when the project moves to another company.">
                  <Tag color="gold">Not in effect yet</Tag>
                </Tooltip>
              ) : null}
              {canEdit && <Button size="small" onClick={() => setEditing(e)}>Edit</Button>}
              {canEdit && <Button size="small" danger onClick={() => removeEntry(e)}>Remove</Button>}
              {logAllowed && <Button size="small" onClick={openLog}>Activity log</Button>}
            </div>
          </div>
          <div style={{ color: "#888", fontSize: 13, margin: "4px 0 10px" }}>
            Oversight of this project. The lead has the project owner&apos;s powers here; each member has the permissions listed.
          </div>
          <Table
            size="small"
            pagination={false}
            rowKey={(m) => m.email || m.name}
            dataSource={e.members || []}
            columns={[
              {
                title: "Name",
                dataIndex: "name",
                render: (v, m) => (
                  <span>
                    {v}
                    {m.lead ? <Tag color="blue" style={{ marginLeft: 6 }}>Lead</Tag> : null}
                  </span>
                ),
              },
              { title: "Email", dataIndex: "email" },
              {
                title: "Permissions",
                dataIndex: "permissions",
                render: (v, m) => (m.lead
                  ? <span style={{ color: "#667085" }}>Every project owner permission</span>
                  : (v || []).length
                    ? (v || []).map((k) => <Tag key={k}>{k}</Tag>)
                    : <span style={{ color: "#999" }}>None</span>),
              },
            ]}
          />
        </div>
      ))}

      {canEdit ? (
        <OversightEditor
          open={!!editing}
          projectId={projectId}
          entry={editing === "new" ? null : editing}
          catalog={catalog}
          onCancel={() => setEditing(null)}
          onSave={saveEntry}
        />
      ) : null}

      <Modal
        open={logOpen}
        title="Oversight activity log"
        onCancel={() => setLogOpen(false)}
        footer={null}
        width={900}
        destroyOnClose
      >
        <div style={{ color: "#888", fontSize: 12.5, marginBottom: 10 }}>
          Every change made through the oversight entry: who, when and what. This log cannot be edited or deleted.
        </div>
        <Table
          size="small"
          loading={logLoading}
          rowKey={(r) => r._id}
          dataSource={log || []}
          pagination={{ pageSize: 25 }}
          columns={[
            { title: "When", dataIndex: "at", width: 170, render: (v) => (v ? new Date(v).toLocaleString() : "") },
            { title: "Who", dataIndex: "actorName", render: (v, r) => v || r.actorEmail || "-" },
            { title: "Action", dataIndex: "action" },
            {
              title: "What",
              dataIndex: "what",
              render: (v, r) => (
                <span style={{ fontSize: 12 }}>
                  {v}
                  {(r.fields || []).length ? <span style={{ color: "#999" }}> ({r.fields.join(", ")})</span> : null}
                </span>
              ),
            },
          ]}
        />
      </Modal>
    </div>
  );
}
